import React, { useState, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityFeatures } from '../../taskList/interfaces';
import AccessibilityPopup from '../../common/components/AccessibilityPopup';
import { ReactComponent as AccessibilityIcon } from '../images/accessibility_icon.svg';
import { AccessibilityLabelStyles } from './../styles/accessibilityLabel';
import { isNotSpaceOrEnterKey } from '../utils/accessibilityGenericMessage';

interface AccessibilityLabelProps {
  accessibilityFeatures: AccessibilityFeatures | undefined;
  className?: string;
  title: string;
  tabDisabled?: boolean;
}

const AccessibilityLabelComponent = (props: AccessibilityLabelProps): JSX.Element => {
  const { t } = useTranslation();
  const { accessibilityFeatures, className, title, tabDisabled } = props;

  const { classes: labelStyles } = AccessibilityLabelStyles();

  const [openA11yPopup, setOpenA11yPopup] = useState<boolean>(false);
  const notSupportedFeatures: string[] = [];
  const accessibilityDialogRef = useRef<HTMLDivElement>(null);

  if (accessibilityFeatures) {
    Object.keys(accessibilityFeatures).forEach((item: string) => {
      if (!accessibilityFeatures[item as keyof AccessibilityFeatures]) {
        notSupportedFeatures.push(item);
      }
      return item;
    });
  }

  if (!notSupportedFeatures.length) return <></>;

  const toggleA11yPopup = (e: React.MouseEvent | React.KeyboardEvent): void => {
    if (isNotSpaceOrEnterKey(e)) return;
    setOpenA11yPopup((prev) => {
      if (!prev) {
        accessibilityDialogRef?.current?.focus();
      }
      return !prev;
    });
  };

  return (
    <span onClick={(e: React.MouseEvent<HTMLElement>): void => e.stopPropagation()}>
      <span
        className={`${labelStyles.accessibilityLabel} ${className ?? ''}`}
        tabIndex={tabDisabled ? -1 : 0}
        aria-disabled={!!tabDisabled}
        onClick={toggleA11yPopup}
        onKeyDown={toggleA11yPopup}>
        <AccessibilityIcon />
        <span>{t('accessibilityNotice')}</span>
      </span>

      <AccessibilityPopup
        title={title}
        notSupportedFeatures={notSupportedFeatures.sort()}
        open={openA11yPopup}
        handleClose={toggleA11yPopup}
        accessibilityDialogRef={accessibilityDialogRef}
      />
    </span>
  );
};

export const AccessibilityLabel = memo(AccessibilityLabelComponent);
